import React, { useMemo } from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import { ReportBuildMetadataFilter } from '@tera/shared/src/types/ReportBuildMetadata';
import { ReportFiltroAtivo } from '../../pages/relatorios/util';
import CampoSeletor, { CampoSeletorOption } from '../Formulario/CampoSeletor';
import CampoMultiSeletor, { CampoMultiSeletorOption } from '../Formulario/CampoMultiSeletor';
import CampoData from '../Formulario/CampoData';
import CampoMemo from '../Formulario/CampoMemo';
import HBox from '../layout/HBox';
import styles from './FiltroRelatorioCampo.module.css';
import Grid, { GridItem } from '../layout/Grid';

type Props = {
    fieldMeta: ReportBuildMetadataFilter;
    reportHref: string;
    value: ReportFiltroAtivo | null;
    onChange: (v: ReportFiltroAtivo) => void;
};

type OperatorOption = CampoSeletorOption;

const DATE_OPERATORS: OperatorOption[] = [
    { id: 'between', label: 'Intervalo' },
    { id: 'eq', label: 'Igual a' },
    { id: 'ge', label: 'A partir de' },
    { id: 'gt', label: 'Depois de' },
    { id: 'le', label: 'Até' },
    { id: 'lt', label: 'Antes de' },
];

const NUMBER_OPERATORS: OperatorOption[] = [
    { id: 'eq', label: 'Igual a' },
    { id: 'ne', label: 'Diferente de' },
    { id: 'gt', label: 'Maior que' },
    { id: 'ge', label: 'Maior ou igual a' },
    { id: 'lt', label: 'Menor que' },
    { id: 'le', label: 'Menor ou igual a' },
    { id: 'between', label: 'Entre' },
    { id: 'in', label: 'É algum de' },
];

const STRING_OPERATORS_FIXED: OperatorOption[] = [
    { id: 'eq', label: 'É igual a' },
    { id: 'ne', label: 'É diferente de' },
    { id: 'in', label: 'É algum de' },
];

const STRING_OPERATORS_FREE: OperatorOption[] = [
    { id: 'ilike', label: 'Contém' },
    { id: 'eq', label: 'É igual a' },
    { id: 'ne', label: 'É diferente de' },
    { id: 'in', label: 'É algum de' },
];

function getOperatorOptions(
    type: string,
    options?: string[],
    lookup?: ReportBuildMetadataFilter['lookup']
): OperatorOption[] {
    if (type === 'date') return DATE_OPERATORS;
    if (lookup) return STRING_OPERATORS_FIXED;
    if (type === 'number') return NUMBER_OPERATORS;
    if (options && options.length > 0) return STRING_OPERATORS_FIXED;
    return STRING_OPERATORS_FREE;
}

export function getDefaultOperator(
    type: string,
    options?: string[],
    lookup?: ReportBuildMetadataFilter['lookup']
): string {
    if (type === 'date') return 'between';
    if (lookup) return 'in';
    if (type === 'number') return 'eq';
    if (options && options.length > 0) return 'eq';
    return 'ilike';
}

const FiltroRelatorioCampo = ({ fieldMeta, reportHref, value, onChange }: Props) => {
    const operatorOptions = useMemo(
        () => getOperatorOptions(fieldMeta.type, fieldMeta.options, fieldMeta.lookup),
        [fieldMeta.type, fieldMeta.options, fieldMeta.lookup]
    );

    const currentOperator = value?.operator ?? getDefaultOperator(fieldMeta.type, fieldMeta.options, fieldMeta.lookup);

    // Busca via lookup de API (ex: /api/canal/box)
    const { data: lookupItems } = useQuery(
        ['lookup', fieldMeta.lookup?.href],
        async () => {
            const res = await axios.get(fieldMeta.lookup!.href);
            return (res.data.result ?? res.data ?? []) as Record<string, any>[];
        },
        {
            enabled: !!fieldMeta.lookup,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Busca valores distintos para campos string sem opções fixas quando operador é 'in'
    const needsDistinct =
        fieldMeta.type === 'string' && !(fieldMeta.options?.length) && !fieldMeta.lookup && currentOperator === 'in';

    const { data: distinctValues } = useQuery(
        ['distinct', reportHref, fieldMeta.name],
        async () => {
            const res = await axios.get(`${reportHref}/distinct?field=${fieldMeta.name}`);
            return (res.data.result ?? []) as string[];
        },
        {
            enabled: needsDistinct,
            staleTime: 5 * 60 * 1000,
        }
    );

    const update = (op: string, val: any) => {
        onChange({ field: fieldMeta.name, type: fieldMeta.type, operator: op, value: val });
    };

    const handleOperatorChange = (op: string | null) => {
        if (!op) return;
        update(op, null); // reset value ao trocar o operador
    };

    const handleValueChange = (val: any) => {
        update(currentOperator, val);
    };

    const renderValueInput = () => {
        const { type, options, lookup } = fieldMeta;
        const currentValue = value?.value;

        // Campo com lookup de API (canal, grupo, usuário etc.)
        if (lookup) {
            const idKey = lookup.idKey ?? 'id';
            const labelKey = lookup.labelKey ?? 'nome';
            const lookupOpts: CampoMultiSeletorOption[] = (lookupItems ?? []).map(item => ({
                id: String(item[idKey]),
                label: item[labelKey] ?? String(item[idKey]),
            }));
            const singleOpts: CampoSeletorOption[] = lookupOpts;

            if (currentOperator === 'in') {
                const selected: any[] = Array.isArray(currentValue) ? currentValue : [];
                return (
                    <div style={{ flex: 1 }}>
                        <CampoMultiSeletor
                            options={lookupOpts}
                            value={selected.map(v => String(v))}
                            onChange={vals => handleValueChange(vals.map(v => Number(v)))}
                            placeholder="Selecionar…"
                        />
                    </div>
                );
            }

            const singleVal = currentValue != null ? String(currentValue) : null;
            return (
                <CampoSeletor
                    options={singleOpts}
                    value={singleVal}
                    onChange={v => handleValueChange(v != null ? Number(v) : null)}
                    placeholder="Selecionar…"
                    nullable
                    style={{ flex: 1 }}
                />
            );
        }

        if (type === 'date') {
            if (currentOperator === 'between') {
                const from = Array.isArray(currentValue) ? (currentValue[0] ?? '') : '';
                const to = Array.isArray(currentValue) ? (currentValue[1] ?? '') : '';
                return (
                    <HBox style={{ gap: 4, alignItems: 'center', flex: 1 }}>
                        <CampoData
                            value={from}
                            onChange={v => handleValueChange([v, to])}
                            placeholder="Data inicial"
                        />
                        <span className={styles.separador}>até</span>
                        <CampoData
                            value={to}
                            onChange={v => handleValueChange([from, v])}
                            placeholder="Data final"
                        />
                    </HBox>
                );
            }
            return (
                <CampoData
                    value={typeof currentValue === 'string' ? currentValue : ''}
                    onChange={handleValueChange}
                    placeholder="Data"
                />
            );
        }

        if (type === 'number') {
            if (currentOperator === 'in') {
                const raw = typeof currentValue === 'string' ? currentValue : '';
                return (
                    <CampoMemo
                        value={raw}
                        onChange={handleValueChange}
                        placeholder="Ex: 123, 456, 789"
                        rows={2}
                        style={{ flex: 1 }}
                    />
                );
            }
            if (currentOperator === 'between') {
                const from = Array.isArray(currentValue) ? (currentValue[0] ?? '') : '';
                const to = Array.isArray(currentValue) ? (currentValue[1] ?? '') : '';
                return (
                    <HBox style={{ gap: 4, alignItems: 'center', flex: 1 }}>
                        <input
                            type="number"
                            className={styles.inputNumero}
                            value={from}
                            onChange={e => handleValueChange([e.target.value, to])}
                            placeholder="De"
                        />
                        <span className={styles.separador}>até</span>
                        <input
                            type="number"
                            className={styles.inputNumero}
                            value={to}
                            onChange={e => handleValueChange([from, e.target.value])}
                            placeholder="Até"
                        />
                    </HBox>
                );
            }
            return (
                <input
                    type="number"
                    className={styles.inputNumero}
                    value={
                        typeof currentValue === 'number' || typeof currentValue === 'string' ? currentValue : ''
                    }
                    onChange={e =>
                        handleValueChange(e.target.value !== '' ? Number(e.target.value) : null)
                    }
                    placeholder="Valor"
                />
            );
        }

        // string
        const fixedOptions = options ?? [];
        const dynamicOpts: CampoMultiSeletorOption[] =
            fixedOptions.length > 0
                ? fixedOptions.map(o => ({ id: o, label: o }))
                : (distinctValues ?? []).map(o => ({ id: o, label: o }));
        const singleOpts: CampoSeletorOption[] = dynamicOpts;

        if (currentOperator === 'in') {
            return (
                <div style={{ flex: 1 }}>
                    <CampoMultiSeletor
                        options={dynamicOpts}
                        value={Array.isArray(currentValue) ? currentValue : []}
                        onChange={handleValueChange}
                        placeholder="Selecionar valores…"
                    />
                </div>
            );
        }

        if (fixedOptions.length > 0) {
            return (
                <CampoSeletor
                    options={singleOpts}
                    value={typeof currentValue === 'string' ? currentValue : null}
                    onChange={handleValueChange}
                    placeholder="Selecionar…"
                    nullable
                    style={{ flex: 1 }}
                />
            );
        }

        // campo texto livre
        return (
            <input
                type="text"
                className={styles.inputTexto}
                value={typeof currentValue === 'string' ? currentValue : ''}
                onChange={e => handleValueChange(e.target.value)}
                placeholder="Valor…"
            />
        );
    };

    return (
        <div className={styles.container}>
            <span className={styles.label}>{fieldMeta.label}</span>
            <Grid>
                <GridItem sm={12} md={4} lg={3}>
                    <CampoSeletor
                        noSearch
                        options={operatorOptions}
                        value={currentOperator}
                        onChange={handleOperatorChange}
                        style={{ minWidth: 150 }}
                    />
                </GridItem>
                <GridItem sm={12} md={8} lg={9}>
                    <div className={styles.valueInputs}>{renderValueInput()}</div>
                </GridItem>
            </Grid>
        </div>
    );
};

export default FiltroRelatorioCampo;
